import { NODE_ENV, REACT_APP_CONVERSED_SERVER_URL } from '@config/env';
import {
  api,
  type TApiInstance,
  type TApisauceRequest,
  type TApisauceResponse,
} from '@conversed/shared/src/wrappers/apisauce';
import { Api } from '../config/api';
import type { DeleteBody, PostBody, PostOptions } from '../types/http';

type TResponse<T> = TApisauceResponse<T>;

let apiInstance: TApiInstance;
let botId: string;

if (!NODE_ENV || NODE_ENV === 'development') {
  apiInstance = api({
    baseURL: REACT_APP_CONVERSED_SERVER_URL || Api.CONVERSED_SERVER,
    headers: {
      'Access-Control-Allow-Origin': '*',
    },
  });
}

export const createHTTPInstance = (url: string, botIdInput: string): void => {
  apiInstance = api({
    baseURL: url,
  });

  botId = botIdInput;
};

// Function to make an API call with optional retry logic
const call = async <T>(
  payload: TApisauceRequest,
  retries: number = Api.RETRY_API_CALL,
): Promise<TResponse<T>> => {
  if (!apiInstance || !botId) {
    throw new Error('Instance not created');
  }

  if (!payload.headers) {
    // Initialize the headers object if it doesn't exist
    payload.headers = {};
  }

  Object.assign(payload.headers, { 'conversed-bot': botId });

  const response = await apiInstance.request<T>(payload);

  // If the request was not successful and there are retries left, retry the request after a delay
  if (!response?.ok && retries > 0) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(call<T>(payload, retries - 1));
      }, 1100);
    });
  }

  return response;
};

export const getRequest = async <T>(url: string): Promise<T | undefined> => {
  const response = await call<T>({
    url: url,
    method: 'GET',
  });

  return response?.data;
};

export const postRequest = async <T>(
  url: string,
  data?: PostBody | string,
  options?: PostOptions,
): Promise<T | undefined> => {
  let headers = {};

  // Deprecated: The needFormUrlEncoded is a deprecated option and should be removed in the future
  if (options?.needFormUrlEncoded) {
    headers = {
      ...headers,
      'Content-Type': 'application/x-www-form-urlencoded',
    };
  }

  const response = await call<T>({
    url: url,
    method: 'POST',
    data: data,
    headers: headers,
  });

  return response?.data;
};

export const deleteRequest = async <T>(
  url: string,
  data?: DeleteBody,
): Promise<T | undefined> => {
  const response = await call<T>({
    url: url,
    method: 'DELETE',
    data: data,
  });

  return response?.data;
};
